// src/components/about/ClientCta.styles.ts
// Closing band. Deliberately inverted against the rest of the page so it
// reads as an ending rather than another content section. Colours are the
// theme's own text/background pair, so it inverts correctly in both themes.
import styled from 'styled-components';
import { motion } from 'framer-motion';

import Button from '../Button';
import { device, minDevice } from '../../styles/breakpoints';

const sansStack = "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif";

// Neutral separator that stays legible on either polarity of the inverted band.
const rule = 'rgba(127, 127, 127, 0.32)';

export const CtaRoot = styled.section`
  padding: ${({ theme }) => theme.spacing['3xl']} 0;
  background: ${({ theme }) => theme.text};
  color: ${({ theme }) => theme.background};

  @media ${minDevice.laptop} {
    padding: 6rem 0;
  }
`;

export const CtaInner = styled.div`
  width: 90%;
  max-width: 1120px;
  margin: 0 auto;
`;

export const CtaGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing['2xl']};
  align-items: start;

  @media ${minDevice.laptop} {
    grid-template-columns: minmax(0, 11fr) minmax(0, 9fr);
    gap: 4rem;
  }
`;

/* ---------- Primary column ---------- */

export const CtaMain = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
  min-width: 0;
`;

export const Eyebrow = styled(motion.p)`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin: 0;
  font-family: ${sansStack};
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.background};
  opacity: 0.62;
`;

export const EyebrowMark = styled.span`
  width: 6px;
  height: 6px;
  flex: 0 0 6px;
  background: ${({ theme }) => theme.background};
`;

export const Heading = styled(motion.h2)`
  margin: 0;
  max-width: 20ch;
  font-family: ${sansStack};
  font-size: 2.1rem;
  line-height: 1.18;
  font-weight: 800;
  letter-spacing: -0.022em;
  color: ${({ theme }) => theme.background};

  @media ${device.mobile} {
    font-size: 1.7rem;
  }
`;

export const Lede = styled(motion.p)`
  margin: 0;
  max-width: 54ch;
  font-family: ${sansStack};
  font-size: 1.02rem;
  line-height: 1.72;
  color: ${({ theme }) => theme.background};
  opacity: 0.78;
`;

export const CtaRow = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.9rem;
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

export const PrimaryCta = styled(Button)`
  button {
    background: ${({ theme }) => theme.background};
    color: ${({ theme }) => theme.text};
    border-color: ${({ theme }) => theme.background};
    font-weight: 600;
  }

  button:hover {
    background: ${({ theme }) => theme.toggleBg};
    color: ${({ theme }) => theme.text};
    border-color: ${({ theme }) => theme.toggleBg};
  }

  button:active {
    background: ${({ theme }) => theme.toggleBg};
    color: ${({ theme }) => theme.text};
  }
`;

export const SecondaryCta = styled(Button)`
  button {
    background: transparent;
    color: ${({ theme }) => theme.background};
    border-color: ${rule};
  }

  button:hover {
    background: rgba(127, 127, 127, 0.16);
    color: ${({ theme }) => theme.background};
    border-color: ${({ theme }) => theme.background};
  }

  button:active {
    background: rgba(127, 127, 127, 0.16);
    color: ${({ theme }) => theme.background};
  }
`;

/* ---------- Capability list ---------- */

export const CtaList = styled(motion.ul)`
  list-style: none;
  margin: 0;
  padding: 0;
  min-width: 0;
`;

export const CtaItem = styled(motion.li)`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: 1rem 0;
  border-top: 1px solid ${rule};

  &:first-child {
    padding-top: 0;
    border-top: none;
  }

  &:last-child {
    padding-bottom: 0;
  }
`;

export const ItemLabel = styled.span`
  font-family: ${sansStack};
  font-size: 0.97rem;
  font-weight: 600;
  line-height: 1.4;
  color: ${({ theme }) => theme.background};
`;

export const ItemDetail = styled.span`
  font-family: ${sansStack};
  font-size: 0.88rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.background};
  opacity: 0.7;
`;
